import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, CheckCircle, Loader } from 'lucide-react';

const InstallationComplete = () => {
  const [installing, setInstalling] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const handleInstall = async () => {
    if (!confirmed) {
      alert("Veuillez confirmer que vous comprenez les implications de cette action.");
      return;
    }

    setInstalling(true);
    setError(null);
    setResult(null);

    try {
      console.log("🚀 Lancement installation complète...");
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/install-complete-from-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        console.log("✅ Installation réussie:", data);
        setResult(data);
      } else {
        console.error("❌ Installation échouée:", data);
        setError(data.message || "Erreur inconnue");
      }
    } catch (err) {
      console.error("💥 Erreur réseau:", err);
      setError(`Erreur réseau: ${err.message}`);
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-3xl font-bold text-center" style={{ color: 'rgb(196, 150, 96)' }}>
            🚀 Installation Complète
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Avertissement */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-yellow-400 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">⚠️ ATTENTION</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>Cette opération va :</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Copier TOUS les membres de Preview vers Production</li>
                    <li>Copier TOUS les événements</li>
                    <li>Copier TOUTES les news</li>
                    <li>Copier TOUS les blasons et images</li>
                    <li>Copier TOUTES les publications</li>
                    <li>Copier TOUS les paramètres</li>
                  </ul>
                  <p className="mt-3 font-semibold">
                    ⚠️ Les données existantes en Production seront REMPLACÉES !
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Checkbox de confirmation */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="confirm"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1 h-4 w-4"
              style={{ accentColor: 'rgb(196, 150, 96)' }}
            />
            <label htmlFor="confirm" className="ml-3 text-sm text-gray-700">
              Je comprends que cette action est <strong>irréversible</strong> et va remplacer toutes les données actuelles en Production par les données de Preview.
            </label>
          </div>

          {/* Bouton d'installation */}
          <Button
            onClick={handleInstall}
            disabled={!confirmed || installing}
            className="w-full text-white text-lg py-6"
            style={{ backgroundColor: confirmed && !installing ? 'rgb(196, 150, 96)' : '#999' }}
          >
            {installing ? (
              <>
                <Loader className="w-5 h-5 mr-2 animate-spin" />
                Installation en cours...
              </>
            ) : (
              '🚀 LANCER L\'INSTALLATION COMPLÈTE'
            )}
          </Button>

          {/* Résultat succès */}
          {result && (
            <div className="bg-green-50 border-l-4 border-green-400 p-4">
              <div className="flex">
                <CheckCircle className="h-5 w-5 text-green-400 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-green-800">✅ Installation réussie !</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>{result.message}</p>
                    {result.stats && (
                      <div className="mt-3 space-y-1">
                        <p><strong>Collections copiées:</strong> {result.stats.collections_copied}</p>
                        <p><strong>Documents copiés:</strong> {result.stats.documents_copied}</p>
                        <p><strong>Fichiers GridFS copiés:</strong> {result.stats.gridfs_files_copied}</p>
                        {result.stats.errors && result.stats.errors.length > 0 && (
                          <p className="text-yellow-600"><strong>Erreurs:</strong> {result.stats.errors.length}</p>
                        )}
                      </div>
                    )}
                    <p className="mt-4 font-semibold">
                      🎉 Vous pouvez maintenant utiliser l'application en Production !
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Résultat erreur */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">❌ Erreur d'installation</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Instructions */}
          {!installing && !result && (
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
              <div className="text-sm text-blue-700">
                <p className="font-semibold mb-2">📋 Instructions :</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Assurez-vous d'être en <strong>Production</strong></li>
                  <li>Vérifiez que Preview contient toutes les données voulues</li>
                  <li>Cochez la case de confirmation</li>
                  <li>Cliquez sur "LANCER L'INSTALLATION COMPLÈTE"</li>
                  <li>Attendez la fin (peut prendre 1-2 minutes)</li>
                </ol>
              </div>
            </div>
          )}

        </CardContent>
      </Card>
    </div>
  );
};

export default InstallationComplete;
